import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import { PublicClientApplication, BrowserCacheLocation } from '@azure/msal-browser';
import './Settings.css';

const INTUNE_PROVISION_SCOPES = [
  'https://graph.microsoft.com/Application.ReadWrite.All',
  'https://graph.microsoft.com/Directory.Read.All'
];

function intuneProvisionErrorText(err) {
  const azureHint =
    err?.errorCode === 'consent_required' || err?.errorCode === 'access_denied'
      ? ' The signed-in account must be a tenant admin and consent to Application.ReadWrite.All and Directory.Read.All.'
      : '';
  return (err?.errorMessage || err?.message || 'Microsoft sign-in failed.') + azureHint;
}

const Settings = ({ onBack }) => {
  const { selectedTenant, hasAllTenantAccess, authenticatedFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantError, setNewTenantError] = useState('');
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [intuneVerifyLoading, setIntuneVerifyLoading] = useState(false);
  const [intuneRegisterLoading, setIntuneRegisterLoading] = useState(false);
  const [intunePermissionsLoading, setIntunePermissionsLoading] = useState(false);
  const [intuneVerifyMessage, setIntuneVerifyMessage] = useState(null);
  const intuneMsalRef = useRef(null);
  
  // Account data for dropdowns
  const [cloudFactoryAccounts, setCloudFactoryAccounts] = useState([]);
  const [dattoAccounts, setDattoAccounts] = useState([]);
  const [zendeskAccounts, setZendeskAccounts] = useState([]);
  const [economicAccounts, setEconomicAccounts] = useState([]);
  
  const [formData, setFormData] = useState({
    // Tenant Status
    IsActive: false,
    
    // Intune Integration
    IntuneTenant: '',
    IntuneClientId: '',
    IntuneClientSecret: '',
    
    // Kept in form state so saves do not clear an existing DB value (no longer editable in UI)
    ITGlueOrgId: '',
    
    // Datto Integration
    DattoSiteUid: '',
    
    // Zendesk Integration
    ZenDeskorganization_id: '',
    
    // CloudFactory Integration
    CFCustomerId: '',
    economicCust: ''
  });

  // Fetch tenant settings and account data on component mount
  useEffect(() => {
    fetchAccountData();
    if (selectedTenant) {
      fetchTenantSettings();
    }
  }, [selectedTenant]);

  const fetchAccountData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      // Fetch all account data in parallel
      const [cfResponse, dattoResponse, zendeskResponse, economicResponse] = await Promise.all([
        fetch(`${getApiBaseUrl()}/api/accounts/cloudfactory`, { headers }),
        fetch(`${getApiBaseUrl()}/api/accounts/datto`, { headers }),
        fetch(`${getApiBaseUrl()}/api/accounts/zendesk`, { headers }),
        fetch(`${getApiBaseUrl()}/api/accounts/economic`, { headers })
      ]);
      
      if (cfResponse.ok) {
        const data = await cfResponse.json();
        setCloudFactoryAccounts(data.accounts || []);
      }
      
      if (dattoResponse.ok) {
        const data = await dattoResponse.json();
        setDattoAccounts(data.accounts || []);
      }
      
      if (zendeskResponse.ok) {
        const data = await zendeskResponse.json();
        setZendeskAccounts(data.accounts || []);
      }

      if (economicResponse.ok) {
        const data = await economicResponse.json();
        setEconomicAccounts(data.accounts || []);
      }
    } catch (err) {
      console.error('Error fetching account data:', err);
    }
  }, []);

  const fetchTenantSettings = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/tenant-settings?tenant=${encodeURIComponent(selectedTenant.name)}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tenant settings');
      }

      const data = await response.json();
      
      // Convert database values to form data
      setFormData({
        IsActive: data.IsActive === 1 || data.IsActive === true,
        IntuneTenant: data.IntuneTenant || '',
        IntuneClientId: data.IntuneClientId || '',
        IntuneClientSecret: data.IntuneClientSecret || '',
        ITGlueOrgId: data.ITGlueOrgId || '',
        DattoSiteUid: data.DattoSiteUid || '',
        ZenDeskorganization_id: data.ZenDeskorganization_id || '',
        CFCustomerId: data.CFCustomerId || '',
        economicCust: data.economicCust != null && data.economicCust !== undefined ? String(data.economicCust) : ''
      });
    } catch (err) {
      console.error('Error fetching tenant settings:', err);
      setError('Failed to load tenant settings');
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  // Fetch tenant settings and account data on component mount
  useEffect(() => {
    fetchAccountData();
    if (selectedTenant) {
      fetchTenantSettings();
    }
  }, [selectedTenant, fetchAccountData, fetchTenantSettings]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Special handling for Intune tenant domain: remove all whitespace
    const sanitizedValue = name === 'IntuneTenant'
      ? value.replace(/\s+/g, '')
      : value;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : sanitizedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedTenant) {
      setError('No tenant selected');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/tenant-settings?tenant=${encodeURIComponent(selectedTenant.name)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to update tenant settings');
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating tenant settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchTenantSettings();
  };

  const handleVerifyIntune = async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      return;
    }
    setIntuneVerifyMessage(null);
    setError(null);
    setIntuneVerifyLoading(true);
    try {
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/tenant-settings/verify-intune?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            IntuneTenant: formData.IntuneTenant,
            IntuneClientId: formData.IntuneClientId,
            IntuneClientSecret: formData.IntuneClientSecret
          })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        const text = data.skipped
          ? (data.message || 'No Intune credentials to verify. Configure tenant domain, client ID, and client secret first.')
          : (data.message || 'Intune credentials verified successfully.');
        setIntuneVerifyMessage({
          type: data.roleCheck && !data.roleCheck.allGranted && data.roleCheck.permissionsConfigured
            ? 'error'
            : 'success',
          text,
          roleCheck: data.roleCheck || null
        });
      } else {
        const msg = data.error || data.message || 'Verification failed';
        setIntuneVerifyMessage({
          type: 'error',
          text: typeof msg === 'string' ? msg : 'Verification failed',
          roleCheck: data.roleCheck || null
        });
      }
    } catch (err) {
      console.error('Verify Intune error:', err);
      setIntuneVerifyMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setIntuneVerifyLoading(false);
    }
  };

  const ensureIntuneProvisioningMsal = async () => {
    if (intuneMsalRef.current) return intuneMsalRef.current;
    const resp = await fetch(`${getApiBaseUrl()}/api/auth/msal/config`);
    if (!resp.ok) {
      throw new Error('Could not load Microsoft sign-in configuration');
    }
    const cfg = await resp.json();
    if (!cfg.clientId) {
      throw new Error('Microsoft sign-in is not configured');
    }
    const client = new PublicClientApplication({
      auth: {
        clientId: cfg.clientId,
        authority: 'https://login.microsoftonline.com/organizations',
        redirectUri: `${window.location.origin}/`
      },
      cache: {
        cacheLocation: BrowserCacheLocation.SessionStorage,
        storeAuthStateInCookie: false
      }
    });
    await client.initialize?.();
    intuneMsalRef.current = client;
    return client;
  };

  const acquireIntuneProvisionToken = async () => {
    const client = await ensureIntuneProvisioningMsal();
    const loginResponse = await client.loginPopup({
      scopes: INTUNE_PROVISION_SCOPES,
      prompt: 'select_account'
    });
    const account =
      loginResponse.account ||
      client.getActiveAccount() ||
      (client.getAllAccounts()[0] || null);
    if (account) client.setActiveAccount(account);

    const tokenResp = await client
      .acquireTokenSilent({
        scopes: INTUNE_PROVISION_SCOPES,
        account: account || undefined
      })
      .catch(() => client.acquireTokenPopup({ scopes: INTUNE_PROVISION_SCOPES, account: account || undefined }));

    const graphAccessToken = tokenResp?.accessToken;
    if (!graphAccessToken) {
      throw new Error('Failed to acquire a Microsoft Graph token. Sign in as a tenant admin.');
    }
    return graphAccessToken;
  };

  const handleRegisterIntuneApp = async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      return;
    }
    setIntuneVerifyMessage(null);
    setError(null);
    setIntuneRegisterLoading(true);
    try {
      const graphAccessToken = await acquireIntuneProvisionToken();

      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/tenant-settings/register-intune-app?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            graphAccessToken,
            redirectUri: `${window.location.origin}/`
          })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        const msg = data.error || data.detail || 'Could not register Portal.SolidIT';
        setIntuneVerifyMessage({
          type: 'error',
          text: typeof msg === 'string' ? msg : 'Could not register Portal.SolidIT',
          detail: data.detail || null,
          consentUrl: null,
          portalUrl: null
        });
        return;
      }

      setFormData((prev) => ({
        ...prev,
        IntuneTenant: data.tenantDomain || prev.IntuneTenant,
        IntuneClientId: data.clientId || prev.IntuneClientId,
        IntuneClientSecret: data.clientSecret || prev.IntuneClientSecret
      }));
      setIntuneVerifyMessage({
        type: 'success',
        text:
          data.message ||
          'Portal.SolidIT was registered as a non-gallery enterprise application. Save settings, then grant admin consent in the Microsoft portal.',
        consentUrl: data.consentUrl || null,
        portalUrl: data.portalUrl || null,
        unmatchedPermissions: data.permissions?.unmatched || []
      });
    } catch (err) {
      console.error('Register Intune app error:', err);
      setIntuneVerifyMessage({
        type: 'error',
        text: intuneProvisionErrorText(err)
      });
    } finally {
      setIntuneRegisterLoading(false);
    }
  };

  const handleAddMissingIntunePermissions = async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      return;
    }
    const clientId = String(formData.IntuneClientId || '').trim();
    if (!clientId) {
      setError('Enter the client ID of the existing Portal.SolidIT app first.');
      return;
    }
    setError(null);
    setIntunePermissionsLoading(true);
    try {
      const graphAccessToken = await acquireIntuneProvisionToken();
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/tenant-settings/add-intune-permissions?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ graphAccessToken, clientId })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        const msg = data.error || data.detail || 'Could not add the missing permissions';
        setIntuneVerifyMessage((prev) => ({
          ...(prev || {}),
          type: 'error',
          text: typeof msg === 'string' ? msg : 'Could not add the missing permissions',
          detail: data.detail || null,
          consentUrl: data.consentUrl || prev?.consentUrl || null,
          portalUrl: data.portalUrl || prev?.portalUrl || null
        }));
        return;
      }

      setIntuneVerifyMessage((prev) => ({
        ...(prev || {}),
        type: 'success',
        text:
          data.message ||
          'Missing permissions were added to the existing Portal.SolidIT app. Grant admin consent, then verify again.',
        detail: null,
        consentUrl: data.consentUrl || null,
        portalUrl: data.portalUrl || null,
        unmatchedPermissions: data.permissions?.unmatched || [],
        permissionsUpdated: true
      }));
    } catch (err) {
      console.error('Add Intune permissions error:', err);
      setIntuneVerifyMessage((prev) => ({
        ...(prev || {}),
        type: 'error',
        text: intuneProvisionErrorText(err)
      }));
    } finally {
      setIntunePermissionsLoading(false);
    }
  };

  const handleToggleActive = async () => {
    const newActiveState = !formData.IsActive;

    if (!selectedTenant) {
      setError('No tenant selected');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (newActiveState) {
        const verifyRes = await authenticatedFetch(
          `${getApiBaseUrl()}/api/tenant-settings/verify-intune?tenant=${encodeURIComponent(selectedTenant.name)}&allowSkipIfIncomplete=1`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              IntuneTenant: formData.IntuneTenant,
              IntuneClientId: formData.IntuneClientId,
              IntuneClientSecret: formData.IntuneClientSecret
            })
          }
        );
        const verifyData = await verifyRes.json().catch(() => ({}));
        if (!verifyRes.ok) {
          setError(verifyData.error || 'Intune verification failed. Fix credentials or try again.');
          setSaving(false);
          return;
        }
        if (verifyData.success === false) {
          setError(verifyData.error || 'Intune verification failed. Cannot activate until credentials obtain a valid token.');
          setSaving(false);
          return;
        }
        if (verifyData.roleCheck?.permissionsConfigured && !verifyData.roleCheck?.allGranted) {
          const missing = (verifyData.roleCheck.missing || []).join(', ');
          setError(
            missing
              ? `Intune verification failed. Missing application permissions: ${missing}`
              : 'Intune verification failed. Required application permissions are missing.'
          );
          setSaving(false);
          return;
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          IsActive: false
        }));
      }

      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/tenant-settings?tenant=${encodeURIComponent(selectedTenant.name)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          IsActive: newActiveState
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update tenant status');
      }

      if (newActiveState) {
        setFormData((prev) => ({
          ...prev,
          IsActive: true
        }));
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating tenant status:', err);
      setError('Failed to update tenant status');
      if (!newActiveState) {
        setFormData((prev) => ({
          ...prev,
          IsActive: true
        }));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTenant = async () => {
    const nameTrimmed = newTenantName.trim();
    if (!nameTrimmed) {
      setError('Tenant name cannot be empty');
      return;
    }
    // Allow only letters, numbers, and spaces
    if (!/^[A-Za-z0-9 ]+$/.test(nameTrimmed)) {
      const message = 'Tenant name may only contain letters, numbers, and spaces';
      setNewTenantError(message);
      setError(message);
      return;
    }
    
    try {
      setCreatingTenant(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/api/tenants/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tenantName: nameTrimmed })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create tenant');
      }

      setSuccess(true);
      setNewTenantName('');
      setNewTenantError('');
      setShowCreateTenant(false);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error creating tenant:', err);
      setError(err.message || 'Failed to create tenant');
    } finally {
      setCreatingTenant(false);
    }
  };

  if (!selectedTenant) {
    return (
      <div className="settings-container">
        <div className="settings-header">
          <button onClick={onBack} className="back-button">
            ← Back to Dashboard
          </button>
          <h1>Settings</h1>
        </div>
        <div className="error-message">Please select a tenant to view settings.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="settings-container">
        <div className="settings-header">
          <button onClick={onBack} className="back-button">
            ← Back to Dashboard
          </button>
          <h1>Settings</h1>
        </div>
        <div className="loading-message">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <div className="settings-header-buttons">
          {hasAllTenantAccess && (
          <button 
            onClick={() => setShowCreateTenant(!showCreateTenant)} 
            className="create-tenant-button"
            disabled={creatingTenant}
          >
            {showCreateTenant ? '✕ Cancel' : '+ Create Tenant'}
          </button>
          )}
          <button onClick={onBack} className="back-button">
            ← Back to Dashboard
          </button>
        </div>
        <div>
          <h1>Integration Settings</h1>
          <p style={{color: 'rgba(255, 255, 255, 0.8)', margin: '0.5rem 0 0 0', fontSize: '1.1rem', fontWeight: '400'}}>
            Configure your tenant integration settings
          </p>
        </div>
      </div>

      {hasAllTenantAccess && showCreateTenant && (
        <div className="create-tenant-form">
          <h3>Create New Tenant</h3>
          <div className="create-tenant-input-group">
            <input
              type="text"
              value={newTenantName}
              onChange={(e) => {
                const next = e.target.value;
                setNewTenantName(next);
                const trimmed = next.trim();
                if (!trimmed) {
                  setNewTenantError('');
                } else if (!/^[A-Za-z0-9 ]+$/.test(trimmed)) {
                  setNewTenantError('Tenant name may only contain letters, numbers, and spaces');
                } else {
                  setNewTenantError('');
                }
              }}
              placeholder="Enter tenant name"
              className="create-tenant-input"
              disabled={creatingTenant}
            />
            {newTenantError && (
              <div className="input-error" style={{ color: '#ff6b6b', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                {newTenantError}
              </div>
            )}
            <button 
              onClick={handleCreateTenant} 
              className="create-tenant-submit"
              disabled={creatingTenant || !newTenantName.trim() || !!newTenantError}
            >
              {creatingTenant ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          Settings saved successfully!
        </div>
      )}

      <form className="settings-form" onSubmit={handleSubmit}>
        {/* Tenant Status */}
        <div className="form-section-compact">
          <div className="section-header-compact">
            <h2>Tenant Status</h2>
          </div>
          <div className="form-group">
            <button
              type="button"
              onClick={handleToggleActive}
              className={`tenant-status-toggle ${formData.IsActive ? 'active' : 'inactive'}`}
              disabled={saving}
            >
              <span className="status-indicator"></span>
              <span className="status-text">
                {formData.IsActive ? 'Active' : 'Disabled'}
              </span>
            </button>
          </div>
        </div>

        {/* Microsoft Intune Integration */}
        <div className="form-section-compact">
          <div className="section-header-compact">
            <h2>Microsoft Intune</h2>
            <span className="section-badge">Authentication</span>
          </div>
          
          <div className="form-grid-3">
            <div className="form-group">
              <label htmlFor="IntuneTenant">Tenant Domain</label>
              <input
                type="text"
                id="IntuneTenant"
                name="IntuneTenant"
                value={formData.IntuneTenant}
                onChange={handleInputChange}
                placeholder="contoso.onmicrosoft.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="IntuneClientId">Client ID</label>
              <input
                type="text"
                id="IntuneClientId"
                name="IntuneClientId"
                value={formData.IntuneClientId}
                onChange={handleInputChange}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </div>
            <div className="form-group">
              <label htmlFor="IntuneClientSecret">Client Secret</label>
              <input
                type="password"
                id="IntuneClientSecret"
                name="IntuneClientSecret"
                value={formData.IntuneClientSecret}
                onChange={handleInputChange}
                placeholder="••••••••••••••••"
              />
            </div>
          </div>
          <div className="intune-verify-actions">
            <div className="intune-verify-button-row">
            <button
              type="button"
              onClick={handleVerifyIntune}
              className="verify-intune-button"
              disabled={saving || intuneVerifyLoading || intuneRegisterLoading || intunePermissionsLoading}
              title="Request an access token from Microsoft using the Intune credentials (saved or entered above)"
            >
              {intuneVerifyLoading ? 'Verifying…' : 'Verify Intune credentials'}
            </button>
            <button
              type="button"
              onClick={handleRegisterIntuneApp}
              className="register-intune-button"
              disabled={saving || intuneVerifyLoading || intuneRegisterLoading || intunePermissionsLoading}
              title="Sign in as a Microsoft tenant admin, register Portal.SolidIT as a non-gallery enterprise app, and fill in the credentials"
            >
              {intuneRegisterLoading ? 'Registering…' : 'Register Portal.SolidIT'}
            </button>
            </div>
            {intuneVerifyMessage && (
              <div className={`intune-verify-hint ${intuneVerifyMessage.type === 'success' ? 'success' : 'error'}`}>
                <p className="intune-verify-hint-text">{intuneVerifyMessage.text}</p>
                {intuneVerifyMessage.detail && (
                  <p className="intune-verify-hint-text">{intuneVerifyMessage.detail}</p>
                )}
                {intuneVerifyMessage.unmatchedPermissions?.length > 0 && (
                  <p className="intune-verify-hint-text">
                    These permissions were not found on Microsoft Graph and were skipped:{' '}
                    {intuneVerifyMessage.unmatchedPermissions.join(', ')}
                  </p>
                )}
                {(intuneVerifyMessage.consentUrl || intuneVerifyMessage.portalUrl) && (
                  <div className="intune-consent-links">
                    {intuneVerifyMessage.consentUrl && (
                      <a
                        href={intuneVerifyMessage.consentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Grant admin consent
                      </a>
                    )}
                    {intuneVerifyMessage.portalUrl && (
                      <a
                        href={intuneVerifyMessage.portalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open Microsoft portal
                      </a>
                    )}
                  </div>
                )}
                {intuneVerifyMessage.roleCheck?.permissionsConfigured && (
                  <div className="intune-role-check">
                    <p className="intune-role-check-title">Application permissions (from access token)</p>
                    <ul className="intune-role-check-list">
                      {(intuneVerifyMessage.roleCheck.required || []).map((perm) => {
                        const ok = (intuneVerifyMessage.roleCheck.granted || []).includes(perm);
                        return (
                          <li key={perm} className={ok ? 'granted' : 'missing'}>
                            <span className="intune-role-check-icon" aria-hidden="true">{ok ? '✓' : '✗'}</span>
                            <span>{perm}</span>
                          </li>
                        );
                      })}
                    </ul>
                    {intuneVerifyMessage.roleCheck.missing?.length > 0 && String(formData.IntuneClientId || '').trim() && (
                      intuneVerifyMessage.permissionsUpdated ? (
                        <p className="intune-role-check-missing">
                          Permissions were added to the existing app. Grant admin consent, then verify again.
                        </p>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={handleAddMissingIntunePermissions}
                            className="register-intune-button add-intune-permissions-button"
                            disabled={saving || intuneVerifyLoading || intuneRegisterLoading || intunePermissionsLoading}
                            title="Sign in as a tenant admin and add the missing Microsoft Graph permissions to the existing app for this client ID"
                          >
                            {intunePermissionsLoading ? 'Adding permissions…' : 'Add missing permissions'}
                          </button>
                          <p className="intune-role-check-missing">
                            Updates the existing Portal.SolidIT app for this client ID. A new registration and a new client secret are not created.
                          </p>
                        </>
                      )
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Service Integrations: only visible to users with access to all tenants (* in TenantAccess) */}
        {hasAllTenantAccess && (
        <div className="form-section-compact">
          <div className="section-header-compact">
            <h2>Service Integrations</h2>
            <span className="section-badge">Organizations</span>
          </div>
          
          <div className="form-grid-2">
            <div className="form-group">
              <label htmlFor="DattoSiteUid">
                <span className="integration-icon">🔒</span> Datto Site
              </label>
              <select
                id="DattoSiteUid"
                name="DattoSiteUid"
                value={formData.DattoSiteUid}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">-- Select Site --</option>
                {dattoAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="ZenDeskorganization_id">
                <span className="integration-icon">🎫</span> Zendesk Organization
              </label>
              <select
                id="ZenDeskorganization_id"
                name="ZenDeskorganization_id"
                value={formData.ZenDeskorganization_id}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">-- Select Organization --</option>
                {zendeskAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="CFCustomerId">
                <span className="integration-icon">☁️</span> CloudFactory Customer
              </label>
              <select
                id="CFCustomerId"
                name="CFCustomerId"
                value={formData.CFCustomerId}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">-- Select Customer --</option>
                {cloudFactoryAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="economicCust">
                <span className="integration-icon">💼</span> e-conomic customer
              </label>
              <select
                id="economicCust"
                name="economicCust"
                value={formData.economicCust}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">-- Select customer number --</option>
                {economicAccounts.map(account => (
                  <option key={account.id} value={String(account.id)}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        )}

        <div className="form-actions">
          <button type="button" onClick={handleReset} className="reset-button" disabled={saving}>
            Reset
          </button>
          <button type="submit" className="save-button" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Settings;